import React from 'react';
import { ArrowRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCategories } from './context/CategoriesContext';

const PromotionalSection = () => {
  const navigate = useNavigate();
  const { categories, loading } = useCategories();

  // Map display categories to actual API categories by name
  const getCategoryByName = (name) => {
    return categories.find(cat =>
      cat.title.toLowerCase().includes(name.toLowerCase()) ||
      name.toLowerCase().includes(cat.title.toLowerCase())
    );
  };

  // Define the categories we want to display with their mappings
  const displayCategories = [
    {
      displayName: "Mobile Cases",
      apiName: "Mobile Cases",
      image: "https://cdn.acsdev.in/ManaPrint/mobilecasespromotion.png",
      bgColor: "bg-amber-100",
      hoverColor: "hover:bg-amber-200"
    },
    {
      displayName: "Fridge Magnets",
      apiName: "Fridge Magnets",
      image: "https://cdn.acsdev.in/ManaPrint/fridgemagnetspromotion.png",
      bgColor: "bg-orange-200",
      hoverColor: "hover:bg-orange-300"
    },
    {
      displayName: "Frames",
      apiName: "Frames",
      image: "https://cdn.acsdev.in/ManaPrint/deskpromotoion.png",
      bgColor: "bg-slate-200",
      hoverColor: "hover:bg-slate-300"
    }
  ];

  // Get the actual categories with their IDs
  const mappedCategories = displayCategories.map(displayCat => {
    const apiCategory = getCategoryByName(displayCat.apiName);
    return {
      ...displayCat,
      id: apiCategory?.id || null,
      title: displayCat.displayName,
      available: !!apiCategory
    };
  }).filter(cat => cat.available); // Only show categories that exist in API

  const handleCategoryClick = (categoryId) => {
    if (categoryId) {
      navigate(`/products#category-${categoryId}`);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <section className="relative overflow-hidden w-full mt-6" style={{ backgroundColor: '#076461' }}>
        <div className="relative container mx-auto px-6 py-6 lg:py-10">
          <div className="flex items-center justify-center h-64">
            <div className="text-white text-lg">Loading categories...</div>
          </div>
        </div>
      </section>
    );
  }

  // If no categories are available, show fallback
  if (mappedCategories.length === 0) {
    return (
      <section className="relative overflow-hidden w-full mt-6" style={{ backgroundColor: '#076461' }}>
        <div className="relative container mx-auto px-6 py-6 lg:py-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-6 lg:space-y-8">
              <div className="space-y-4">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm font-medium backdrop-blur-sm">
                  <Sparkles size={16} />
                  <span>Featured Categories</span>
                </div>

                <h1 className="text-4xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight" style={{ fontFamily: 'Gloock, serif' }}>
                  Top Picks for{' '}
                  <span className="relative">
                    <span className="relative z-10 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                      Custom
                    </span>
                    <div className="absolute inset-0 bg-yellow-400/20 blur-lg rounded-lg"></div>
                  </span>
                  <br />
                  Creations
                </h1>

                <p className="text-lg lg:text-xl text-white/90 leading-relaxed max-w-lg">
                  Browse our most-loved categories and shop your next favorite design.
                  Transform your ideas into reality with our premium printing services.
                </p>
              </div>

              <div className="pt-4 flex justify-center lg:justify-start">
                <button onClick={()=>navigate('/products')} className="group inline-flex items-center gap-3 px-8 py-4 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
                  <span>View All Products</span>
                  <ArrowRight
                    size={20}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="text-white/70 text-center">
                Categories will appear here once loaded
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative overflow-hidden w-full mt-6" style={{ backgroundColor: '#076461' }}>
      {/* Decorative Pattern Background */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Ccircle cx='30' cy='30' r='4'/%3E%3Ccircle cx='0' cy='30' r='4'/%3E%3Ccircle cx='60' cy='30' r='4'/%3E%3Ccircle cx='30' cy='0' r='4'/%3E%3Ccircle cx='30' cy='60' r='4'/%3E%3Ccircle cx='0' cy='0' r='4'/%3E%3Ccircle cx='60' cy='0' r='4'/%3E%3Ccircle cx='0' cy='60' r='4'/%3E%3Ccircle cx='60' cy='60' r='4'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }}
      />

      {/* Floating Decorative Elements */}
      <div className="absolute top-8 left-8 text-white/20">
        <Sparkles size={32} />
      </div>
      <div className="absolute bottom-8 right-8 text-white/20">
        <Sparkles size={24} />
      </div>
      <div className="absolute top-1/2 left-4 text-white/10">
        <div className="w-16 h-16 rounded-full border-2 border-white/20"></div>
      </div>
      <div className="absolute top-1/4 right-4 text-white/10">
        <div className="w-12 h-12 rounded-full bg-white/10"></div>
      </div>

      <div className="relative container mx-auto px-6 py-6 lg:py-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Side - Title and Description */}
          <div className="space-y-6 lg:space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-white/90 text-sm font-medium backdrop-blur-sm">
                <Sparkles size={16} />
                <span>Featured Categories</span>
              </div>

              <h1 className="text-4xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight" style={{ fontFamily: 'Gloock, serif' }}>
                Top Picks for{' '}
                <span className="relative">
                  <span className="relative z-10 bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent">
                    Custom
                  </span>
                  <div className="absolute inset-0 bg-yellow-400/20 blur-lg rounded-lg"></div>
                </span>
                <br />
                Creations
              </h1>

              <p className="text-lg lg:text-xl text-white/90 leading-relaxed max-w-lg">
                Browse our most-loved categories and shop your next favorite design.
                Transform your ideas into reality with our premium printing services.
              </p>
            </div>

            {/* View All Button */}
            <div className="pt-4 flex flex-row justify-center lg:justify-start">
              <button onClick={()=>navigate('/products')} className="group inline-flex items-center gap-3 px-8 py-4 bg-yellow-500 hover:bg-yellow-400 text-black font-semibold rounded-full transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
                <span>View All Products</span>
                <ArrowRight
                  size={20}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          </div>

          {/* Right Side - Category Cards */}
          <div className="relative">
            {/* Background Glow Effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/5 to-white/10 rounded-3xl blur-xl"></div>

            <div className="relative grid gap-6 lg:gap-8 max-w-md mx-auto">
              {/* First Row - Single Card */}
              {mappedCategories[0] && (
                <div className="w-full">
                  <CategoryCard
                    category={mappedCategories[0]}
                    index={0}
                    onClick={() => handleCategoryClick(mappedCategories[0].id)}
                  />
                </div>
              )}

              {/* Second Row - Two Cards */}
              {mappedCategories.length > 1 && (
                <div className="grid grid-cols-1 gap-4 lg:gap-6">
                  {mappedCategories[1] && (
                    <CategoryCard
                      category={mappedCategories[1]}
                      index={1}
                      onClick={() => handleCategoryClick(mappedCategories[1].id)}
                    />
                  )}
                  {mappedCategories[2] && (
                    <CategoryCard
                      category={mappedCategories[2]}
                      index={2}
                      onClick={() => handleCategoryClick(mappedCategories[2].id)}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Animated Wave Pattern */}
      <div className="absolute bottom-0 left-0 right-0 overflow-hidden">
        <div className="wave-container">
          {/* First wave layer */}
          <div className="wave-layer wave-layer-1">
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="wave-svg">
              <path
                d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
                opacity=".25"
                className="fill-white"
              />
            </svg>
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="wave-svg">
              <path
                d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
                opacity=".25"
                className="fill-white"
              />
            </svg>
          </div>

          {/* Second wave layer */}
          <div className="wave-layer wave-layer-2">
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="wave-svg">
              <path
                d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
                opacity=".5"
                className="fill-white"
              />
            </svg>
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="wave-svg">
              <path
                d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
                opacity=".5"
                className="fill-white"
              />
            </svg>
          </div>

          {/* Third wave layer */}
          <div className="wave-layer wave-layer-3">
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="wave-svg">
              <path
                d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
                className="fill-white"
              />
            </svg>
            <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="wave-svg">
              <path
                d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
                className="fill-white"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Styles for animations */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .wave-container {
          position: relative;
          width: 100%;
          height: 48px;
          overflow: hidden;
          transform: scaleY(-1);
        }

        @media (min-width: 1024px) {
          .wave-container {
            height: 64px;
          }
        }

        .wave-layer {
          position: absolute;
          top: 0;
          left: 0;
          width: 200%;
          height: 100%;
          display: flex;
        }

        .wave-svg {
          width: 50%;
          height: 100%;
          flex-shrink: 0;
        }

        @keyframes wave-move-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          }
        }

        @keyframes wave-move-right {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(50%);
          }
        }

        .wave-layer-1 {
          animation: wave-move-left 20s linear infinite;
        }

        .wave-layer-2 {
          animation: wave-move-right 15s linear infinite;
        }

        .wave-layer-3 {
          animation: wave-move-left 25s linear infinite;
        }

        .wave-animation {
          animation: none;
        }
      `}</style>
    </section>
  );
};

// Category Card Component
const CategoryCard = ({ category, index, onClick }) => {
  return (
    <div
      className={`group relative overflow-hidden rounded-2xl lg:rounded-3xl ${category.bgColor} ${category.hoverColor} transition-all duration-500 transform hover:scale-105 hover:shadow-2xl cursor-pointer w-full`}
      style={{
        animationDelay: `${index * 150}ms`,
        animation: 'fadeInUp 0.8s ease-out forwards'
      }}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={`Browse ${category.title} category`}
    >
      {/* Card Content */}
      <div className="relative p-6 lg:p-8 h-48 lg:h-56 flex flex-col justify-between">
        {/* Image Container */}
        <div className="flex-1 flex items-center justify-center">
          <div className="relative">
            <img
              src={category.image}
              alt={category.title}
              className="w-20 h-20 lg:w-28 lg:h-28 object-contain transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3"
            />
            {/* Glow Effect */}
            <div className="absolute inset-0 bg-white/20 rounded-full blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        </div>

        {/* Title */}
        <div className="text-center">
          <h3 className="text-sm lg:text-base font-bold text-zinc-700 group-hover:text-zinc-800 transition-colors duration-300">
            {category.title}
          </h3>
        </div>

        {/* Hover Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl lg:rounded-3xl"></div>

        {/* Decorative Corner */}
        <div className="absolute top-0 right-0 w-8 h-8 bg-white/20 rounded-bl-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Click indicator */}
        <div className="absolute inset-0 border-2 border-transparent group-hover:border-white/30 rounded-2xl lg:rounded-3xl transition-all duration-300"></div>
      </div>
    </div>
  );
};

export default PromotionalSection;
